import React from 'react';
import { motion } from 'framer-motion';

const steps = [
  {
    number: "1",
    emoji: "📱",
    title: "Open the app and hit Record",
    description: "Prop your phone against a fence or bag. Press record, walk onto the court, and just play your normal game."
  },
  {
    number: "2",
    emoji: "🤖",
    title: "We analyze your match",
    description: "Our AI reviews the footage and finds patterns — weak spots, strong shots, footwork issues — all automatically."
  },
  {
    number: "3",
    emoji: "📈",
    title: "See what to work on",
    description: "Get a simple breakdown of your game and a short list of drills to run before your next match. That's it."
  }
];

const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-24 bg-neutral-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-20 items-center">

          {/* Text Content */}
          <div className="mb-14 lg:mb-0">
            <h2 className="text-primary font-semibold tracking-wide uppercase text-sm mb-3">How It Works</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-text-primary mb-5">
              As easy as pressing <span className="text-teal">Record.</span>
            </h3>
            <p className="text-text-secondary text-lg mb-10">
              No complicated setup. No technical skills needed. If you can film a video, you can use Pickleball Insight Coach.
            </p>

            <div className="space-y-8">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.12 }}
                  className="flex gap-5 items-start"
                >
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-2xl">
                    {step.emoji}
                  </div>
                  <div className="pt-1">
                    <h4 className="text-lg font-bold text-text-primary mb-1">{step.title}</h4>
                    <p className="text-text-secondary leading-relaxed">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden shadow-2xl border-8 border-white bg-white"
          >
            <div className="absolute inset-0 bg-primary/10 mix-blend-multiply z-10" />
            <img
              src="https://images.unsplash.com/photo-1622228519450-48e025a1f6a1?auto=format&fit=crop&q=80&w=1000"
              alt="Pickleball Player"
              className="w-full h-auto object-cover aspect-square"
            />
            {/* Overlay Badge */}
            <div className="absolute bottom-6 left-6 right-6 bg-white/97 backdrop-blur z-20 rounded-2xl p-5 shadow-lg border border-neutral-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">✅ Analysis Complete</p>
                  <p className="text-base font-bold text-text-primary">3 drills ready for you</p>
                  <p className="text-sm text-text-secondary mt-0.5">Your dink game improved <span className="text-utility-success font-semibold">+15%</span> this week</p>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default HowItWorks;